import { Search } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

export type EmptyStateProps = {
  /**
   * Icon component (typically a Lucide icon), rendered inside a tinted circle.
   * Defaults to a search glyph.
   */
  Icon?: React.ComponentType<{ className?: string; "aria-hidden"?: true }>;
  /** Primary line — short, sentence case. */
  title?: string;
  /** Optional sub-line — longer explanation or hint. */
  description?: React.ReactNode;
  /** Optional CTA — typically a button or a card. */
  action?: React.ReactNode;
  /**
   * Wrap the message in a dashed bordered box with a fixed minimum height.
   * Defaults to `true` for the framed list/table placeholder look. Pass
   * `false` for inline / contextual empties that should blend with their
   * surrounding content (e.g. inside a results stream).
   */
  bordered?: boolean;
  children?: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "title">;

/**
 * Centered empty / zero-result message: tinted icon circle, headline, optional
 * sub-line, and an optional CTA (via `action` or `children`). Defaults to the
 * framed list/table placeholder look; pass `bordered={false}` for inline use.
 */
export function EmptyState({
  Icon = Search,
  title = "No results",
  description = "Try adjusting your search or filters",
  action,
  bordered = true,
  children,
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 px-4 py-12 text-center",
        bordered &&
          "h-56 justify-center gap-4 rounded-xl border-2 border-border border-dashed bg-muted/30",
        className
      )}
      data-slot="empty-state"
      {...rest}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full bg-muted/50 text-muted-foreground",
          bordered ? "size-14" : "size-10"
        )}
      >
        <Icon aria-hidden className="size-5" />
      </span>
      <div className="space-y-1">
        <h3 className="font-medium text-sm">{title}</h3>
        {description ? (
          <p className="max-w-sm text-muted-foreground text-sm">
            {description}
          </p>
        ) : null}
      </div>
      {action}
      {children}
    </div>
  );
}
